import FINANCIAL_STATUS_DICT from '~/server_shared/generated-types/FINANCIAL_STATUS_DICT';

/**
 * Financial statuses whose only writer is split routing.
 *
 * A parent payment derives `partially_failed` from its sub-payments, and sub-payments exist only
 * where the organization's `is_split_routing_enabled` toggle is on -- so at an organization with
 * the toggle off no payment can carry the status, and offering it in a filter puts a value in
 * front of the user that matches nothing.
 *
 * Hand-written because nothing declares it: `enums.json` says what a financial status *is*, not
 * which configuration makes it reachable. The one mirror is the backend's
 * `payment_defs.SPLIT_ROUTING_ONLY_FINANCIAL_STATUSES`, which gates its own picker off the same
 * toggle.
 */
const SPLIT_ROUTING_ONLY_FINANCIAL_STATUSES: readonly string[] = ['partially_failed'];

/**
 * The financial statuses this organization's users may pick from.
 *
 * Every declared status, less the ones only split routing can produce when the organization has
 * split routing off. The members come from `FINANCIAL_STATUS_DICT`, so a status added later is
 * offered without an edit here.
 *
 * Only the pickers narrow. The surfaces that render a *stored* status -- the status chip, the
 * `desc` lookups on a payment row -- keep reading the full dict: `is_split_routing_enabled` is a
 * live toggle, so an organization can turn split routing off with `partially_failed` rows already
 * on the books, and taking the value out of a filter must not stop those rows from rendering.
 * The backend's `payment_defs.get_selectable_financial_statuses` is the same derivation over the
 * same declaration, read off the same configuration.
 */
export const getSelectableFinancialStatuses = (isSplitRoutingEnabled: boolean): string[] =>
  Object.keys(FINANCIAL_STATUS_DICT).filter(
    (financialStatus) => isSplitRoutingEnabled || !SPLIT_ROUTING_ONLY_FINANCIAL_STATUSES.includes(financialStatus)
  );
